import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Loader2, CheckCircle2, Mail, ArrowRight, Download } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';

export default function GiftVoucherSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [voucher, setVoucher] = useState(null);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setError('Sessione di pagamento mancante'); return; }
    let cancelled = false;
    const poll = async (n) => {
      try {
        const res = await api.get(`/vouchers/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.status === 'paid' || res.data.status === 'redeemed') { setVoucher(res.data); return; }
        if (res.data.status === 'cancelled') { setError('Il pagamento non è andato a buon fine.'); return; }
        if (n >= 10) { setError('Il pagamento è ancora in elaborazione: riceverai il buono via email appena confermato.'); return; }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Errore nella verifica del pagamento');
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="pt-32 pb-24 min-h-screen bg-[#f6f1ec] text-[#2b2521]" data-testid="gift-voucher-success-page">
      <div className="max-w-2xl mx-auto px-6 text-center">
        {!voucher && !error && (
          <div className="py-24" data-testid="voucher-checking">
            <Loader2 className="mx-auto animate-spin text-[#8a6f5f]" size={40} />
            <p className="mt-6 text-[#5b4f48]">Stiamo confermando il pagamento{attempts > 0 ? '...' : ''}</p>
          </div>
        )}
        {error && (
          <div className="py-24" data-testid="voucher-error">
            <p className="text-lg text-[#5b4f48]">{error}</p>
            <Link to="/buono-regalo"><Button className="mt-8" variant="outline">Torna al buono regalo</Button></Link>
          </div>
        )}
        {voucher && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <CheckCircle2 className="mx-auto text-emerald-600" size={48} />
            <h1 className="font-heading text-4xl sm:text-5xl mt-6">Grazie, {voucher.buyer_name}!</h1>
            <p className="mt-4 text-[#5b4f48]">Il buono regalo per <strong className="text-[#2b2521]">{voucher.recipient_name}</strong> è pronto.</p>
            <div className="mt-10 bg-white p-10 shadow-sm" data-testid="voucher-card">
              <p className="text-xs uppercase tracking-[0.2em] text-[#8a6f5f] flex items-center justify-center gap-2"><Gift size={14} /> Buono Regalo</p>
              <p className="font-heading text-2xl mt-3">{voucher.package}</p>
              <p className="text-4xl font-bold mt-4">€ {Number(voucher.amount).toFixed(0)}</p>
              <p className="inline-block mt-6 bg-[#2b2521] text-[#f6f1ec] px-6 py-3 tracking-[0.2em] text-lg" data-testid="voucher-code">{voucher.code}</p>
              {voucher.message && <p className="mt-6 italic text-[#5b4f48]">“{voucher.message}”</p>}
              <p className="mt-6 text-xs text-[#8a6f5f]">Valido fino al {new Date(voucher.expires_at).toLocaleDateString('it-IT')}</p>
            </div>
            <p className="mt-8 text-sm text-[#5b4f48] flex items-center justify-center gap-2" data-testid="voucher-email-note">
              <Mail size={16} /> Ti abbiamo inviato il buono via email{voucher.recipient_email ? ` (e anche a ${voucher.recipient_name})` : ''}.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              {voucher.pdf_url && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}${voucher.pdf_url}`} target="_blank" rel="noopener noreferrer" data-testid="voucher-pdf-btn">
                  <Button className="bg-[#c9a27a] text-[#2b2521] hover:bg-[#b8905f]"><Download className="mr-2" size={16} /> Scarica il buono da stampare (PDF)</Button>
                </a>
              )}
              <Link to="/prenota?servizio=Maternity%20%2B%20Newborn"><Button className="bg-[#2b2521] text-[#f6f1ec] hover:bg-[#5b4f48]">Prenota con il buono <ArrowRight className="ml-2" size={16} /></Button></Link>
              <Link to="/"><Button variant="outline" className="border-[#2b2521]">Torna alla home</Button></Link>
            </div>
            <p className="mt-4 text-xs text-[#8a6f5f]">Stampalo in A5 (o A4 e ritaglia) e mettilo in una busta: è pronto per il giorno della festa.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
